/* crop button hotfix (Vania 7 Oct 2026 · 11:20 UTC · live QA) ·
 * imperative replacement for window.confirm / window.prompt / window.alert.
 *
 * The Claude desktop app's embedded webview silently suppresses native
 * dialogs so admin presses did nothing. This shim renders our
 * ConfirmDialog/PromptDialog overlay in a detached root and returns a
 * Promise, so the call sites stay 1-to-1:
 *
 *     const yes = await confirm("Delete this venue?");
 *     const reason = await prompt("Why reject?");  // null = cancelled
 *     await alert("Done");
 */
import React from "react";
import { createRoot } from "react-dom/client";
import { ConfirmDialog, PromptDialog } from "@/components/admin/ConfirmDialog";

function openOverlay(renderComponent) {
  const el = document.createElement("div");
  document.body.appendChild(el);
  const root = createRoot(el);
  const cleanup = () => {
    try { root.unmount(); } catch { /* unmount race */ }
    if (el.parentNode) el.parentNode.removeChild(el);
  };
  return new Promise((resolve) => {
    root.render(renderComponent({ resolve, cleanup }));
  });
}

/**
 * Returns Promise<boolean>. `true` = confirmed, `false` = cancelled.
 * Pass a string message or an options object:
 *   confirm("Delete?")
 *   confirm({ title: "Delete?", message: "...", tone: "danger", confirmLabel: "yes" })
 */
export function confirm(messageOrOpts) {
  const opts = typeof messageOrOpts === "string"
    ? { message: messageOrOpts }
    : (messageOrOpts || {});
  return openOverlay(({ resolve, cleanup }) => (
    <ConfirmDialog
      open={true}
      title={opts.title}
      message={opts.message}
      confirmLabel={opts.confirmLabel}
      cancelLabel={opts.cancelLabel}
      tone={opts.tone}
      testIdPrefix={opts.testIdPrefix || "confirm"}
      onConfirm={() => { resolve(true); cleanup(); }}
      onCancel={() => { resolve(false); cleanup(); }}
    />
  ));
}

/**
 * Returns Promise<string|null>. The resolved value is the input text,
 * or `null` if the user cancelled (matches native `window.prompt`).
 */
export function prompt(messageOrOpts, defaultValue = "") {
  const opts = typeof messageOrOpts === "string"
    ? { message: messageOrOpts, defaultValue }
    : (messageOrOpts || {});
  if (opts.defaultValue === undefined) opts.defaultValue = defaultValue || "";
  return openOverlay(({ resolve, cleanup }) => (
    <PromptDialog
      open={true}
      title={opts.title}
      message={opts.message}
      placeholder={opts.placeholder || ""}
      defaultValue={opts.defaultValue || ""}
      confirmLabel={opts.confirmLabel}
      cancelLabel={opts.cancelLabel}
      testIdPrefix={opts.testIdPrefix || "prompt"}
      onConfirm={(value) => { resolve(value); cleanup(); }}
      onCancel={() => { resolve(null); cleanup(); }}
    />
  ));
}

/** Promise-based info dialog. Resolves when the user acknowledges. */
export function alert(messageOrOpts) {
  const opts = typeof messageOrOpts === "string"
    ? { message: messageOrOpts }
    : (messageOrOpts || {});
  return openOverlay(({ resolve, cleanup }) => (
    <ConfirmDialog
      open={true}
      title={opts.title}
      message={opts.message}
      confirmLabel={opts.confirmLabel || "ok"}
      cancelLabel={opts.cancelLabel || "ok"}
      testIdPrefix={opts.testIdPrefix || "alert"}
      onConfirm={() => { resolve(true); cleanup(); }}
      onCancel={() => { resolve(true); cleanup(); }}
    />
  ));
}
